import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  Input,
  Text,
  HStack,
  Badge,
  Box,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCompleteSession } from 'hooks/useSessions';
import type { Session, CompleteSessionDto } from 'types/models';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
  patientId: string;
  episodeId: string;
  planId: string;
}

function SectionTitle({ label, color }: { label: string; color: string }) {
  return (
    <HStack spacing="8px" mb="2px">
      <Badge colorScheme={color} borderRadius="full" px="10px" py="2px" fontSize="xs" fontWeight="700">
        {label}
      </Badge>
    </HStack>
  );
}

export default function CompleteSessionModal({ isOpen, onClose, session, patientId, episodeId, planId }: Props) {
  const { mutate: complete, isPending } = useCompleteSession();

  const sectionBg   = useColorModeValue('gray.50', 'navy.750');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor  = useColorModeValue('gray.600', 'gray.400');

  // ─── Subjetivo ──────────────────────────────────────────────────────────────
  const [motivoSesion, setMotivoSesion]           = useState('');
  const [evaDolor, setEvaDolor]                   = useState('');
  const [sintomasReferidos, setSintomasReferidos] = useState('');
  const [motivoError, setMotivoError]             = useState(false);

  // ─── Objetivo ───────────────────────────────────────────────────────────────
  const [hallazgos, setHallazgos]       = useState('');
  const [rom, setRom]                   = useState('');
  const [fuerza, setFuerza]             = useState('');
  const [otrosObj, setOtrosObj]         = useState('');

  // ─── Análisis ───────────────────────────────────────────────────────────────
  const [diagnostico, setDiagnostico]           = useState('');
  const [progreso, setProgreso]                 = useState('');
  const [respuestaTrat, setRespuestaTrat]       = useState('');

  // ─── Plan ───────────────────────────────────────────────────────────────────
  const [tecnicas, setTecnicas]                     = useState('');
  const [ejerciciosInd, setEjerciciosInd]           = useState('');
  const [objetivosProx, setObjetivosProx]           = useState('');

  // ─── General ────────────────────────────────────────────────────────────────
  const [observaciones, setObservaciones] = useState('');

  const handleSubmit = () => {
    if (motivoSesion.trim().length < 5) {
      setMotivoError(true);
      return;
    }
    setMotivoError(false);

    const payload: CompleteSessionDto = {
      subjetivo: {
        motivoSesion: motivoSesion.trim(),
        evaDolor: evaDolor !== '' ? Number(evaDolor) : undefined,
        sintomasReferidos: sintomasReferidos.trim() || undefined,
      },
      objetivo: {
        hallazgosExamenFisico: hallazgos.trim() || undefined,
        rangoMovimiento: rom.trim() || undefined,
        fuerzaMuscular: fuerza.trim() || undefined,
        otrosHallazgos: otrosObj.trim() || undefined,
      },
      analisis: (diagnostico || progreso || respuestaTrat)
        ? {
            diagnosticoFisioterapeutico: diagnostico.trim() || undefined,
            progresoVsAnterior: progreso.trim() || undefined,
            respuestaTratamiento: respuestaTrat.trim() || undefined,
          }
        : undefined,
      plan: (tecnicas || ejerciciosInd || objetivosProx)
        ? {
            tecnicasAplicadas: tecnicas.trim() || undefined,
            ejerciciosIndicados: ejerciciosInd.trim() || undefined,
            objetivosProximaSesion: objetivosProx.trim() || undefined,
          }
        : undefined,
      observaciones: observaciones.trim() || undefined,
    };

    complete(
      { patientId, episodeId, planId, sessionId: session.id, payload },
      { onSuccess: onClose },
    );
  };

  const fieldProps = {
    bg: useColorModeValue('white', 'gray.700'),
    borderRadius: '8px',
    fontSize: 'sm',
    size: 'sm' as const,
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" fontWeight="700">
          Completar Sesión #{session.numeroSesion}
          <Text fontSize="xs" fontWeight="400" color={labelColor} mt="2px">
            {new Date(session.fechaSesion).toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' })}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb="4">
          <VStack spacing="16px" align="stretch">

            {/* ── S — Subjetivo ───────────────────────────────────────────── */}
            <Box bg={sectionBg} border="1px solid" borderColor={borderColor} borderRadius="10px" p="14px">
              <SectionTitle label="S — Subjetivo" color="blue" />
              <VStack spacing="10px" align="stretch" mt="10px">
                <FormControl isRequired isInvalid={motivoError}>
                  <FormLabel fontSize="xs" color={labelColor} mb="4px">Motivo / evolución de sesión</FormLabel>
                  <Textarea
                    value={motivoSesion}
                    onChange={e => { setMotivoSesion(e.target.value); setMotivoError(false); }}
                    placeholder="Describe la evolución del paciente en esta sesión..."
                    rows={3}
                    resize="none"
                    {...fieldProps}
                  />
                  <FormErrorMessage fontSize="xs">Mínimo 5 caracteres.</FormErrorMessage>
                </FormControl>

                <HStack spacing="10px" align="flex-start">
                  <FormControl maxW="110px">
                    <FormLabel fontSize="xs" color={labelColor} mb="4px">EVA Dolor (0-10)</FormLabel>
                    <Input
                      type="number"
                      min={0} max={10}
                      value={evaDolor}
                      onChange={e => setEvaDolor(e.target.value)}
                      placeholder="—"
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel fontSize="xs" color={labelColor} mb="4px">Síntomas referidos</FormLabel>
                    <Input
                      value={sintomasReferidos}
                      onChange={e => setSintomasReferidos(e.target.value)}
                      placeholder="Hormigueo, parestesias, irradiación..."
                      {...fieldProps}
                    />
                  </FormControl>
                </HStack>
              </VStack>
            </Box>

            {/* ── O — Objetivo ────────────────────────────────────────────── */}
            <Box bg={sectionBg} border="1px solid" borderColor={borderColor} borderRadius="10px" p="14px">
              <SectionTitle label="O — Objetivo" color="green" />
              <VStack spacing="10px" align="stretch" mt="10px">
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor} mb="4px">Hallazgos examen físico</FormLabel>
                  <Textarea
                    value={hallazgos}
                    onChange={e => setHallazgos(e.target.value)}
                    placeholder="Limitación ROM, signos clínicos, palpación..."
                    rows={2}
                    resize="none"
                    {...fieldProps}
                  />
                </FormControl>
                <HStack spacing="10px">
                  <FormControl flex="1">
                    <FormLabel fontSize="xs" color={labelColor} mb="4px">Rango de movimiento</FormLabel>
                    <Input
                      value={rom}
                      onChange={e => setRom(e.target.value)}
                      placeholder="Flexión L: 60°..."
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel fontSize="xs" color={labelColor} mb="4px">Fuerza muscular</FormLabel>
                    <Input
                      value={fuerza}
                      onChange={e => setFuerza(e.target.value)}
                      placeholder="Glúteo mayor 4/5..."
                      {...fieldProps}
                    />
                  </FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor} mb="4px">Otros hallazgos</FormLabel>
                  <Input
                    value={otrosObj}
                    onChange={e => setOtrosObj(e.target.value)}
                    placeholder="Observaciones adicionales..."
                    {...fieldProps}
                  />
                </FormControl>
              </VStack>
            </Box>

            {/* ── A — Análisis ─────────────────────────────────────────────── */}
            <Box bg={sectionBg} border="1px solid" borderColor={borderColor} borderRadius="10px" p="14px">
              <SectionTitle label="A — Análisis" color="orange" />
              <VStack spacing="10px" align="stretch" mt="10px">
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor} mb="4px">Diagnóstico fisioterapéutico</FormLabel>
                  <Input
                    value={diagnostico}
                    onChange={e => setDiagnostico(e.target.value)}
                    placeholder="Síndrome radicular L4-L5 fase subaguda..."
                    {...fieldProps}
                  />
                </FormControl>
                <HStack spacing="10px">
                  <FormControl flex="1">
                    <FormLabel fontSize="xs" color={labelColor} mb="4px">Progreso vs sesión anterior</FormLabel>
                    <Input
                      value={progreso}
                      onChange={e => setProgreso(e.target.value)}
                      placeholder="Aumento ROM 45° a 60°..."
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel fontSize="xs" color={labelColor} mb="4px">Respuesta al tratamiento</FormLabel>
                    <Input
                      value={respuestaTrat}
                      onChange={e => setRespuestaTrat(e.target.value)}
                      placeholder="Buena tolerancia a tracción lumbar..."
                      {...fieldProps}
                    />
                  </FormControl>
                </HStack>
              </VStack>
            </Box>

            {/* ── P — Plan ─────────────────────────────────────────────────── */}
            <Box bg={sectionBg} border="1px solid" borderColor={borderColor} borderRadius="10px" p="14px">
              <SectionTitle label="P — Plan" color="purple" />
              <VStack spacing="10px" align="stretch" mt="10px">
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor} mb="4px">Técnicas aplicadas</FormLabel>
                  <Input
                    value={tecnicas}
                    onChange={e => setTecnicas(e.target.value)}
                    placeholder="Tracción lumbar 20min, TENS 15min..."
                    {...fieldProps}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor} mb="4px">Ejercicios indicados</FormLabel>
                  <Input
                    value={ejerciciosInd}
                    onChange={e => setEjerciciosInd(e.target.value)}
                    placeholder="Williams x10 reps, Puente glúteo x15..."
                    {...fieldProps}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor} mb="4px">Objetivos próxima sesión</FormLabel>
                  <Input
                    value={objetivosProx}
                    onChange={e => setObjetivosProx(e.target.value)}
                    placeholder="Alcanzar flexión 75°, reducir EVA a 3..."
                    {...fieldProps}
                  />
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            <FormControl>
              <FormLabel fontSize="xs" color={labelColor} mb="4px">Observaciones generales</FormLabel>
              <Textarea
                value={observaciones}
                onChange={e => setObservaciones(e.target.value)}
                placeholder="Notas adicionales sobre la sesión..."
                rows={2}
                resize="none"
                {...fieldProps}
              />
            </FormControl>

          </VStack>
        </ModalBody>

        <ModalFooter gap="10px">
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isPending}>
            Cancelar
          </Button>
          <Button
            colorScheme="green"
            size="sm"
            onClick={handleSubmit}
            isLoading={isPending}
            loadingText="Guardando..."
          >
            Completar sesión
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
